import useUsers from "src/api/hooks/useUsers";
import useGroups from "src/api/hooks/useGroups";
import SearchInput from "src/components/SearchInput";
import { useEffect, useState } from "react";
import { DataCard } from "src/components/DataCard";
import { Modal } from "src/components/Modal";
import { Button } from "src/components/Buttons/Button";
import { UserResponse } from "src/types/UserResponse";
import { lang } from "src/language/Strings";
import { ShowToast } from "src/components/Toast";
import { useMutation } from "@tanstack/react-query";
import { portalApiClient } from "src/api/apiClient";
import { apiUrls } from "src/consts/ApiUrls";
import { queryKeys } from "src/api/queryKeys";
import { queryClient } from "src/main";
import { useForm } from "src/api/hooks/useForm";
import { useParams } from "react-router-dom";
import { ItemDescription } from "src/components/ItemDescription";
import { useEntraUsers } from "src/api/hooks/useUsersFromEntra";
import useChangeTitle from "src/api/hooks/useChangeTitle";
import { useUserById } from "src/api/hooks/useUsers";

export function UsersPage() {
  const { data: users } = useUsers();

  const { data: groups } = useGroups();
  const [searchText, setSearchText] = useState("");
  const { companyId } = useParams();
  useChangeTitle(lang.users);
  useEffect(() => {
    const plusElementHandler = () => {
      setSelectedUser({
        firstName: "",
        surname: "",
        username: "",
        mobileNumber: "",
        objectId: "",
        userRole: 6,
        companyId: Number(companyId),
        type: "Entra",
        groupIds: [],
      });
    };
    const plusElement = document.getElementById("plus-icon-add");
    if (plusElement) {
      plusElement.addEventListener("click", plusElementHandler);
      return () => {
        plusElement.removeEventListener("click", plusElementHandler);
      };
    }
  }, []);

  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  return (
    <div className="flex flex-col h-full relative w-full ">
      <div className="flex flex-row w-full p-2">
        <div className="h-full w-full overflow-y-auto">
          <SearchInput onChange={setSearchText} />
          {users
            ?.filter((user) =>
              `${user.firstName.toLowerCase()} ${user.surname.toLowerCase()}`.includes(
                searchText.toLowerCase()
              )
            )
            .map((user) => (
              <DataCard
                key={"user-" + user.id}
                id={user.id.toString()}
                imageUrl={""}
                header={`${user.firstName} ${user.surname}`}
                firstRowInput={user.username}
                secondRowInput={user.mobileNumber}
                thirdRowInput={
                  user.roleId % 2 === 0 ? lang.user : lang.companyAdmin
                }
                onClick={() => {
                  setSelectedUser(user);
                }}
                isSelected={false}
              />
            ))}
        </div>
      </div>
      {selectedUser ? (
        <UserModal
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
        />
      ) : null}
    </div>
  );
}

function UserModal({
  selectedUser,
  setSelectedUser,
}: {
  selectedUser: UserResponse | null;
  setSelectedUser: (user: UserResponse | null) => void;
}) {
  const { companyId } = useParams();
  const { Form, formData } = useForm({
    firstName: selectedUser?.firstName || "",
    surname: selectedUser?.surname || "",
    username: selectedUser?.username || "",
    mobileNumber: selectedUser?.mobileNumber || "",

    groupIds: selectedUser?.groupIds || [],
    type: selectedUser?.type || "B2C",
    userRole: selectedUser?.userRole || 5,
    companyId: Number(selectedUser?.companyId) || Number(companyId),
    id: selectedUser?.id || undefined,
    ...(selectedUser?.id ? {} : { objectId: "" }),
  });

  const createMutation = useMutation({
    mutationFn: async (user: UserResponse) => {
      if (user.id) {
        const response = await portalApiClient.put(apiUrls.users, user);
        return response.data;
      }
      const response = await portalApiClient.post(apiUrls.users, user);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users(companyId) });
      setSelectedUser(null);
      ShowToast(
        selectedUser?.id === undefined
          ? lang.createUserSuccess
          : lang.saveUserSuccess,
        "success",
        3000
      );
    },
    onError: () => {
      ShowToast(lang.saveUserError, "error", 3000);
    },
    mutationKey: ["userMutation", ...queryKeys.users()],
  });
  return (
    selectedUser && (
      <Modal onOutsideClick={() => setSelectedUser(null)}>
        <h1>
          {selectedUser.firstName} {selectedUser.surname}
        </h1>
        <Form id="userForm">
          <ItemDescription label={lang.firstName}>
            <Form.Input name="firstName" defaultValue={formData.firstName} />
          </ItemDescription>
          <ItemDescription label={lang.surname}>
            <Form.Input name="surname" defaultValue={formData.surname} />
          </ItemDescription>
          <ItemDescription label={lang.username}>
            <Form.Input name="username" defaultValue={formData.username} />
          </ItemDescription>
          <ItemDescription label={lang.phone}>
            <Form.Input
              name="mobileNumber"
              defaultValue={formData.mobileNumber}
            />
          </ItemDescription>
          {!selectedUser?.id ? (
            <ItemDescription label={lang.objectId}>
              <Form.Input name="objectId" defaultValue={formData.objectId} />
            </ItemDescription>
          ) : null}
          <ItemDescription label={lang.role}>
            <Form.Select name="userRole" defaultValue={formData.userRole}>
              <Form.Select.Option value={5}>
                {lang.companyAdmin}
              </Form.Select.Option>
              <Form.Select.Option value={6}>{lang.user}</Form.Select.Option>
            </Form.Select>
          </ItemDescription>
        </Form>
        <div className="flex flex-row gap-2">
          <Button
            onClick={() => setSelectedUser(null)}
            title={lang.close}
            buttonType="button"
          />
          <Button
            title={selectedUser?.id === undefined ? lang.create : lang.save}
            buttonType="submit"
            onClick={() => {
              createMutation.mutate(formData);
            }}
            formId="userForm"
          />
        </div>
      </Modal>
    )
  );
}
